import axios from 'axios';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { FiMail, FiShield, FiTruck, FiCheckCircle, FiClock, FiArrowRight, FiRefreshCw } from 'react-icons/fi';
import loginStateAtom from '../atoms/loginStateAtom';
import { app_version, url } from '../utils/config';
import myInfoAtom from '../atoms/myInfoAtom';
import { setLoader } from '../reusable_components/Loader';

function Login(props) {
    const [loggedIn, setLoggedIn] = useRecoilState(loginStateAtom);
    const [myInfo, setMyInfo] = useRecoilState(myInfoAtom);
    const [loginScreen, setLoginScreen] = useState(true);
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [uid, setUid] = useState("");
    const [isRequestingOtp, setIsRequestingOtp] = useState(false);
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const [emailFocused, setEmailFocused] = useState(false);
    const [otpFocused, setOtpFocused] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let interval;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const toggleGlobalLoader = (state) => {
        if (typeof setLoader === 'function') {
            setLoader(state);
        }
    };

    const features = [
        { icon: FiTruck, title: 'Fleet Tracking', desc: 'Real-time vehicle monitoring' },
        { icon: FiCheckCircle, title: 'Service Management', desc: 'Streamlined maintenance' },
    { icon: FiClock, title: 'Smart Reminders', desc: 'Never miss a deadline' },
    ];


    async function getOtp() {
        if (!email || !email.match('@') || !email.match('.')) {
            toast.error('Please provide a valid Email!', { autoClose: 3000 });
            return;
        }

        try {
            setIsRequestingOtp(true);
            toggleGlobalLoader(true);
            const reqUrl = url + '/get_otp';
            const { data } = await axios.post(reqUrl, { email });
            setUid(data.uid);
            setResendTimer(120); // 2 minutes timer
            setTimeout(() => {
                setLoginScreen(false);
            }, 200);
        } catch (error) {
            const message = error?.response?.data?.message || 'Unable to generate verification code.';
            toast.error(message, { autoClose: 3000 });
        } finally {
            setIsRequestingOtp(false);
            toggleGlobalLoader(false);
        }
    }

    async function phoneLogin() {
        if (!otp) {
            toast.error('Please enter the verification code.', { autoClose: 3000 });
            return;
        }

        const reqUrl = url + '/login';
        try {
            setIsVerifyingOtp(true);
            toggleGlobalLoader(true);
            const { data } = await axios.post(reqUrl, { email, otp, uid });
            window.localStorage.setItem('fm_token', data.token);
            window.localStorage.setItem('role', data.info.role);
            window.localStorage.setItem('loggedInAs', data.info.loggedInAs);
            setMyInfo(data.info);
            setLoggedIn(true);
            navigate('/');
        } catch (error) {
            const message = error?.response?.data?.message || 'Unable to verify the code.';
            toast.error(message, { autoClose: 3000 });
        } finally {
            setIsVerifyingOtp(false);
            toggleGlobalLoader(false);
        }
    }

    function handleGetOtp(e) {
        if (e.key === 'Enter') {
            getOtp()
        }
    }

    function handleLogin(e) {
        if (e.key === 'Enter') {
            phoneLogin()
        }
    }


    return (
        <div className='min-h-screen w-full flex items-stretch overflow-hidden'>
            {/* Left Side - Branding & Features - FleetIO Style */}
            <div 
                className='hidden lg:flex lg:basis-1/2 flex-col items-center justify-center p-8 relative'
                style={{
                    background: 'linear-gradient(135deg, #27AE60 0%, #1E8449 50%, #16603D 100%)'
                }}
            >
                {/* Animated background shapes */}
                <div className='absolute inset-0 overflow-hidden'>
                    <div className='absolute -top-16 -left-16 w-56 h-56 bg-white/5 rounded-full blur-3xl animate-pulse' />
                    <div className='absolute -bottom-24 -right-16 w-72 h-72 bg-green-300/10 rounded-full blur-3xl animate-pulse' style={{ animationDelay: '1s' }} />
                </div>

                <div className='relative z-10 max-w-md'>
                    {/* Logo/Brand - FleetIO Style */}
                    <div className='flex items-center gap-3 mb-8'>
                        <div className='w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md border border-white/30'>
                            <FiTruck className='w-6 h-6 text-white' />
                        </div>
                        <div>
                            <h1 className='text-xl font-bold text-white tracking-tight'>Roll and Charge Fleet Manager</h1>
                            <p className='text-green-100 text-xs'>Fleet Management Platform</p>
                        </div>
                    </div>

                    {/* Tagline */}
                    <h2 className='text-4xl font-bold text-white leading-snug mb-4'>
                        Manage Your Fleet<br />
                        <span className='text-green-200'>Reliably</span>
                    </h2>
                    <p className='text-green-100/90 text-base mb-8'>
                        Run your fleet reliably, repair your assets quickly, and optimize every aspect of your operation.
                    </p>

                    {/* Feature Cards */}
                    <div className='grid grid-cols-2 gap-4'>
                        {features.map((feature, idx) => (
                            <div 
                                key={idx}
                                className='group bg-white/8 backdrop-blur-sm rounded-xl p-4 border border-white/15 hover:bg-white/15 hover:border-white/25 transition-all duration-300 cursor-default'
                            >
                                <feature.icon className='w-5 h-5 text-green-200 mb-2 group-hover:scale-110 transition-transform' />
                                <h3 className='text-white font-semibold text-sm'>{feature.title}</h3>
                                <p className='text-green-100/60 text-xs mt-1'>{feature.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Trust badge */}
                    <div className='mt-8 flex items-center gap-2 text-green-100/70 text-sm'>
                        <FiShield className='w-4 h-4' />
                        <span>Trusted by 1,000,000+ fleet assets</span>
                    </div>
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div 
                className='flex-1 flex items-center justify-center p-4 sm:p-6 bg-neutral-50'
            >
                <div className='w-full max-w-md'>
                    {/* Mobile Logo */}
                    <div className='lg:hidden flex items-center justify-center gap-3 mb-8'>
                        <div className='w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-md'>
                            <FiTruck className='w-5 h-5 text-white' />
                        </div>
                        <span className='text-lg font-bold text-primary'>Roll and Charge Fleet Manager</span>
                    </div>

                    {/* Login Card */}
                    <div
                        className='bg-white rounded-xl shadow-base border border-neutral-200 overflow-hidden'
                    >
                        {/* Progress indicator - FleetIO Navy Blue */}
                        <div className='h-1 bg-neutral-200'>
                            <div 
                                className='h-full bg-primary transition-all duration-500 ease-out'
                                style={{ width: loginScreen ? '50%' : '100%' }}
                            />
                        </div>

                        <div className='px-8 py-8'>
                            {loginScreen ? (
                                <div className='space-y-6'>
                                    {/* Header */}
                                    <div className='text-center'>
                                        <div className='w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center mx-auto mb-4'>
                                            <FiMail className='w-6 h-6 text-primary' />
                                        </div>
                                        <h2 className='text-2xl font-bold text-primary'>Welcome Back</h2>
                                        <p className='text-neutral-600 text-sm mt-2'>Enter your email to continue</p>
                                    </div>

                                    {/* Email Input */}
                                    <div className='relative'>
                                        <label className='block text-sm font-semibold text-primary mb-2'>
                                            Email Address
                                        </label>
                                        <div className={`relative rounded-lg transition-all duration-200 ${emailFocused ? 'ring-2 ring-primary ring-offset-2' : ''}`}>
                                            <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
                                                <FiMail className={`w-5 h-5 transition-colors ${emailFocused ? 'text-primary' : 'text-neutral-400'}`} />
                                            </div>
                                            <input
                                                type='email'
                                                className='w-full pl-12 pr-4 py-3 bg-neutral-50 border border-neutral-300 rounded-lg text-sm text-neutral-900 placeholder-neutral-500 focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary-light transition-all'
                                                placeholder='you@company.com'
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                onKeyDown={handleGetOtp}
                                                onFocus={() => setEmailFocused(true)}
                                                onBlur={() => setEmailFocused(false)}
                                            />
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        className='w-full py-3 px-4 bg-primary hover:bg-primary-dark text-white text-sm font-semibold rounded-lg shadow-base transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-60 disabled:cursor-not-allowed'
                                        onClick={getOtp}
                                        disabled={isRequestingOtp}
                                    >
                                        {isRequestingOtp ? (
                                            <>
                                                <FiRefreshCw className='w-4 h-4 animate-spin' />
                                                Sending Code...
                                            </>
                                        ) : (
                                            <>
                                                Continue
                                                <FiArrowRight className='w-4 h-4 group-hover:translate-x-1 transition-transform' />
                                            </>
                                        )}
                                    </button>

                                    {/* Security note */}
                                    <p className='text-center text-xs text-neutral-500'>
                                        <FiShield className='w-3 h-3 inline mr-1.5' />
                                        We'll send a secure verification code to your email
                                    </p>
                                </div>
                            ) : (
                                <div className='space-y-6'>
                                    {/* Header */}
                                    <div className='text-center'>
                                        <div className='w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center mx-auto mb-4 relative'>
                                            <FiShield className='w-6 h-6 text-primary' />
                                            <div className='absolute -top-1 -right-1 w-5 h-5 bg-secondary rounded-full flex items-center justify-center'>
                                                <FiMail className='w-2.5 h-2.5 text-white' />
                                            </div>
                                        </div>
                                        <h2 className='text-2xl font-bold text-primary'>Verify Your Email</h2>
                                        <p className='text-neutral-600 text-sm mt-2'>
                                            We sent a code to <span className='font-semibold text-primary'>{email}</span>
                                        </p>
                                    </div>

                                    {/* OTP Input */}
                                    <div className='relative'>
                                        <label className='block text-sm font-semibold text-primary mb-2'>
                                            Verification Code
                                        </label>
                                        <div className={`relative rounded-lg transition-all duration-200 ${otpFocused ? 'ring-2 ring-primary ring-offset-2' : ''}`}>
                                            <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
                                                <FiShield className={`w-5 h-5 transition-colors ${otpFocused ? 'text-primary' : 'text-neutral-400'}`} />
                                            </div>
                                            <input
                                                type='text'
                                                className='w-full pl-12 pr-4 py-3 bg-neutral-50 border border-neutral-300 rounded-lg text-neutral-900 text-center tracking-[0.4em] font-mono text-lg placeholder-neutral-500 focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary-light transition-all'
                                                placeholder='••••••'
                                                value={otp}
                                                onChange={e => setOtp(e.target.value)}
                                                onKeyDown={handleLogin}
                                                onFocus={() => setOtpFocused(true)}
                                                onBlur={() => setOtpFocused(false)}
                                                maxLength={6}
                                            />
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        className='w-full py-3 px-4 bg-primary hover:bg-primary-dark text-white text-sm font-semibold rounded-lg shadow-base transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-60 disabled:cursor-not-allowed'
                                        onClick={phoneLogin}
                                        disabled={isVerifyingOtp}
                                    >
                                        {isVerifyingOtp ? (
                                            <>
                                                <FiRefreshCw className='w-4 h-4 animate-spin' />
                                                Verifying...
                                            </>
                                        ) : (
                                            <>
                                                Verify & Login
                                                <FiCheckCircle className='w-4 h-4 group-hover:scale-110 transition-transform' />
                                            </>
                                        )}
                                    </button>

                                    {/* Resend */}
                                    <div className='text-center'>
                                        <p className='text-sm text-neutral-600'>
                                            Didn't receive the code?
                                        </p>
                                        {resendTimer > 0 ? (
                                            <div className='mt-2 inline-flex items-center gap-2 px-4 py-2 bg-primary-light rounded-lg'>
                                                <FiClock className='w-4 h-4 text-primary' />
                                                <span className='text-sm font-semibold text-primary'>
                                                    Resend in {formatTimer(resendTimer)}
                                                </span>
                                            </div>
                                        ) : (
                                            <button
                                                className='mt-2 text-primary font-semibold text-sm hover:text-primary-dark hover:underline transition-colors'
                                                onClick={getOtp}
                                            >
                                                Resend verification code
                                            </button>
                                        )}
                                    </div>

                                    {/* Back button */}
                                    <button
                                        className='w-full py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors'
                                        onClick={() => setLoginScreen(true)}
                                    >
                                        ← Use a different email
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className='px-6 py-3 bg-primary-light border-t border-primary-light flex items-center justify-between'>
                            <span className='text-[10px] text-primary'>Secure login with Roll and Charge Fleet Manager</span>
                            <span className='text-[10px] text-gray-400'>{app_version}</span>
                        </div>
                    </div>

                    {/* Bottom links */}
                    <p className='text-center text-[10px] text-gray-500 mt-4'>
                        By continuing, you agree to our{' '}
                        <a href='#' className='text-primary hover:underline'>Terms of Service</a>
                        {' '}and{' '}
                        <a href='#' className='text-primary hover:underline'>Privacy Policy</a>
                    </p>
                </div>
            </div>
        </div>
    )

}

export default Login;